import { saveAs } from 'file-saver';

/**
 * Utility functions for CipherCanvas
 */

// Export functions
export const exportAsText = (content, filename = 'cipher-result.txt') => {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  saveAs(blob, filename);
};

export const exportAsJSON = (data, filename = 'cipher-config.json') => {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json;charset=utf-8' });
  saveAs(blob, filename);
};

// Text manipulation utilities
export const scrambleText = (text, duration = 500) => {
  const chars = '!@#$%^&*()_+-=[]{}|;:,.<>?';
  const originalText = text;
  
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      const scrambled = originalText
        .split('')
        .map((char) => {
          if (Math.random() > 0.7) {
            return chars[Math.floor(Math.random() * chars.length)];
          }
          return char;
        })
        .join('');
      // This would be used for visual effects in animations
      console.log(scrambled);
    }, 50);
    
    setTimeout(() => {
      clearInterval(interval);
      resolve(originalText);
    }, duration);
  });
};

// Validation utilities
export const validateJSON = (jsonString) => {
  try {
    JSON.parse(jsonString);
    return { valid: true, error: null };
  } catch (error) {
    return { valid: false, error: error.message };
  }
};

export const validateKey = (key, keyType) => {
  switch (keyType) {
    case 'number':
      const num = parseInt(key);
      return !isNaN(num) && num >= 0 && num <= 25;
    case 'text':
      return typeof key === 'string' && key.length > 0;
    case 'textarea':
      return typeof key === 'string';
    default:
      return true;
  }
};

// Animation utilities
export const getRandomDelay = (min = 0, max = 0.5) => {
  return Math.random() * (max - min) + min;
};

export const staggerChildren = (index, stagger = 0.1) => {
  return {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: { delay: index * stagger }
    }
  };
};

// Theme utilities
export const getThemeColors = (darkMode = true) => {
  if (darkMode) {
    return {
      background: 'bg-gray-900',
      surface: 'bg-gray-800',
      text: 'text-gray-100',
      accent: 'text-neon-cyan',
      border: 'border-neon-cyan/30'
    };
  } else {
    return {
      background: 'bg-gray-100',
      surface: 'bg-white',
      text: 'text-gray-900',
      accent: 'text-blue-600',
      border: 'border-blue-300'
    };
  }
};

// Local storage utilities
export const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
    return false;
  }
};

export const loadFromLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Failed to load from localStorage:', error);
    return defaultValue;
  }
};

// Text analysis utilities
export const analyzeText = (text) => {
  return {
    length: text.length,
    words: text.split(/\s+/).filter(word => word.length > 0).length,
    characters: text.replace(/\s/g, '').length,
    lines: text.split('\n').length,
    entropy: calculateEntropy(text)
  };
};

const calculateEntropy = (text) => {
  const freq = {};
  for (const char of text) {
    freq[char] = (freq[char] || 0) + 1;
  }
  
  let entropy = 0;
  const length = text.length;
  
  for (const count of Object.values(freq)) {
    const p = count / length;
    entropy -= p * Math.log2(p);
  }
  
  return entropy.toFixed(2);
};
